import React, { FC, useMemo, useState, useEffect } from "react";
import { FootballProps } from "..";
import { RawPlayer, Lineups, PlayerVM, Metric } from "./types";
import CategoryBar from "./components/CategoryBar";
import MetricTitle from "./components/MetricTitle";
import DualView from "./components/DualView";
import ListView from "./components/ListView";
import { getTeamColors } from "components/colors";

const normalizeKit = (k?: any) => {
  if (!k) return undefined;
  const id = k.id ?? k.ID ?? k.kitId ?? k.KitID ?? k.kid ?? k.KID;
  const si =
    (typeof k.si === "string" && k.si.trim() !== "" ? k.si : undefined) ??
    (typeof k.SI === "string" && k.SI.trim() !== "" ? k.SI : undefined) ??
    (Array.isArray(k.colors) && k.colors.length
      ? k.colors.join(",")
      : undefined) ??
    (typeof k.palette === "string" && k.palette.trim() !== ""
      ? k.palette
      : undefined);
  if (id == null || si == null) return undefined;
  return { id, si } as const;
};

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const countFromDelimited = (v: any): number => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return 0;
    const n = Number(t);
    if (Number.isFinite(n)) return n;
    // supports formats like "12|Shot;" or "44|Shot;68|Header;"
    return t.split(";").filter(Boolean).length;
  }
  return 0;
};

const pickName = (p: RawPlayer) => {
  if (p?.n) return p.n;
  const first = (p?.n2 || "").trim();
  const last = (p?.n1 || "").trim();
  return `${first} ${last}`.trim() || "-";
};

const METRIC_ORDER: Metric[] = [
  "list",
  "assists",
  "crosses",
  "fouls",
  "goals",
  "keyPasses",
  "passAccuracy",
  "passes",
  "red",
  "yellow",
  "shots",
  "tackles",
  "xg",
  "saves",
];

const hasValue = (metric: Metric, p: any) => {
  if (metric === "list") return true;
  if (metric === "xg") return Number(p.xg || 0) > 0;
  if (metric === "passAccuracy") return Number(p.passAccuracy || 0) > 0;
  return Number(p[metric] || 0) > 0;
};

const PlayerStats: FC<FootballProps> = ({ status }) => {
  const [metric, setMetric] = useState<Metric>("list");
  const { home: homeColor, away: awayColor } = getTeamColors(status as any);

  const homeKit =
    normalizeKit((status as any)?.T1?.kit) ||
    normalizeKit((status as any)?.t1?.kit) ||
    undefined;

  const awayKit =
    normalizeKit((status as any)?.T2?.kit) ||
    normalizeKit((status as any)?.t2?.kit) ||
    undefined;

  const { home, away, hasAnyData } = useMemo(() => {
    const rawLp = ((status as any)?.lp ||
      (status as any)?.c?.lp ||
      undefined) as Lineups | undefined;

    const h = (rawLp?.h?.ply || {}) as Record<string, RawPlayer>;
    const a = (rawLp?.a?.ply || {}) as Record<string, RawPlayer>;

    const mapPlayers = (players: Record<string, RawPlayer>) =>
      Object.entries(players).map(([id, p]) => {
        const passes = toNum(p?.pa);
        const tackles = toNum(p?.ta);
        const fouls = toNum((p as any)?.fouls);
        const xg = Number.isFinite(Number(p?.xg)) ? Number(p?.xg) : 0;

        const assists = toNum(p?.as);
        const goals = countFromDelimited(p?.goal);
        const yellow = countFromDelimited(p?.ycard);
        const red = countFromDelimited(p?.rcard);
        const subsCount = countFromDelimited((p as any)?.subs);
        const crosses = toNum((p as any)?.cr);
        const keyPasses = toNum((p as any)?.kp);
        const saves = toNum((p as any)?.sv);
        const shots = toNum(p?.sh);
        const passAccuracy = toNum((p as any)?.paAcc);

        return {
          id,
          name: pickName(p),
          passes,
          tackles,
          fouls,
          xg,
          assists,
          goals,
          yellow,
          red,
          crosses,
          keyPasses,
          saves,
          shots,
          passAccuracy,
          isSub: !!p?.sub,
          ...(subsCount ? { subs: subsCount } : {}),
        } as PlayerVM;
      });

    const homeArr = mapPlayers(h);
    const awayArr = mapPlayers(a);
    const hasAny = homeArr.length > 0 || awayArr.length > 0;

    return { home: homeArr, away: awayArr, hasAnyData: hasAny };
  }, [status]);

  const availableMetrics: Metric[] = useMemo(() => {
    const combined = [...home, ...away];
    const set = new Set<Metric>(["list"]);
    METRIC_ORDER.forEach((m) => {
      if (m === "list") return;
      if (combined.some((p) => hasValue(m, p))) set.add(m);
    });
    return METRIC_ORDER.filter((m) => set.has(m));
  }, [home, away]);

  useEffect(() => {
    if (!availableMetrics.includes(metric)) setMetric("list");
  }, [availableMetrics, metric]);

  const [homeSorted, awaySorted] = useMemo(() => {
    const key = metric;
    const sortBy = (arr: any[]) =>
      [...arr].sort((x, y) => Number(y[key] || 0) - Number(x[key] || 0));
    return [sortBy(home), sortBy(away)];
  }, [home, away, metric]);

  const badgeValue = (p: any): string => {
    switch (metric) {
      case "xg":
        return p.xg ? p.xg.toFixed(2) : "-";
      case "passAccuracy":
        return p.passAccuracy ? `${p.passAccuracy}%` : "-";
      default: {
        const v = p[metric];
        return v && Number(v) !== 0 ? String(v) : "-";
      }
    }
  };
  const isBadgeEmpty = (p: any): boolean => {
    switch (metric) {
      case "xg":
        return !(p.xg && Number(p.xg) !== 0);
      case "passAccuracy":
        return !(p.passAccuracy && Number(p.passAccuracy) !== 0);
      case "list":
        return false;
      default: {
        const v = p[metric];
        return !(v && Number(v) !== 0);
      }
    }
  };

  if (!hasAnyData) {
    return null;
  }
  return (
    <div className="text-white">
      <CategoryBar
        metric={metric}
        setMetric={setMetric}
        availableMetrics={availableMetrics}
      />
      <MetricTitle metric={metric} />
      {metric === "list" ? (
        <ListView
          rowsHome={home as PlayerVM[]}
          rowsAway={away as PlayerVM[]}
          homeColor={homeColor}
          awayColor={awayColor}
          homeKit={homeKit}
          awayKit={awayKit}
        />
      ) : (
        <DualView
          homeSorted={homeSorted as PlayerVM[]}
          awaySorted={awaySorted as PlayerVM[]}
          badgeValue={badgeValue as (p: PlayerVM) => string}
          isBadgeEmpty={isBadgeEmpty as (p: PlayerVM) => boolean}
        />
      )}
    </div>
  );
};

export default PlayerStats;
